// src/pages/Attendance/OnboardingForm.tsx

import React, { useEffect, useState } from "react";
import Step1BasicInfo from "./Step1BasicInfo";
import Step2EmployeePosition from "./Step2EmployeePosition";
import Step3PaymentMode from "./Step3PaymentMode";
import { fetchManagers } from "../../store/slices/leaveSlice";
import { fetchUserNames } from "../../store/slices/userSlice";
import { useDispatch } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { fetchAttendanceMetadata } from "../../store/slices/attendanceSlice";
import ProgressIndicator from "./ProgressIndicator";
import {
  fetchEmployeeDetails,
  submitEmployeeDetails,
} from "../../store/slices/employeeSlice";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

const AddEmployeeForm = () => {
  const dispatch = useDispatch<RootDispatch>();
  const [formData, setFormData] = useState<any>({});
  const [currentStep, setCurrentStep] = useState<number>(1);

  useEffect(() => {
    dispatch(fetchManagers());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchUserNames());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchAttendanceMetadata());
  }, [dispatch]);

  const handleUpdate = (updatedFields: any) => {
    setFormData((prev: any) => ({ ...prev, ...updatedFields }));
  };

  useEffect(() => {
    if (formData.userId) {
      dispatch(fetchEmployeeDetails(formData.userId));
    }
  }, [dispatch, formData.userId]);

  const fetchedDetails = useSelector(
    (state: RootState) => state.employee.userDetails
  );
  useEffect(() => {
    if (formData.userId === undefined) return;
    
    if (fetchedDetails && Object.keys(fetchedDetails).length > 0) {
      setFormData(fetchedDetails);
    } else {
      setFormData({ userId: formData.userId });
  }
}, [fetchedDetails]);

const handleSubmit = async () => {
  if (!formData.aadharName || formData.aadharName.trim().length < 3) {
    toast.error("Aadhaar Name must be at least 3 characters.");
    return;
  }

  if (!formData.fatherName || formData.fatherName.trim().length < 3) {
    toast.error("Father's Name must be at least 3 characters.");
    return;
  }

  if (!formData.dob || isNaN(Date.parse(formData.dob))) {
    toast.error("Please enter a valid Date of Birth.");
    return;
  }

  if (!["male", "female", "other"].includes(formData.gender)) {
    toast.error("Please select a valid Gender.");
    return;
  }
  if (!/^[A-Z]{5}\d{4}[A-Z]$/.test(formData.pan || "")) {
    toast.error("Please enter a valid PAN (e.g. ABCDE1234F).");
    return;
  }

  if (!formData.email || !/^\S+@\S+\.\S+$/.test(formData.email)) {
    toast.error("Please enter a valid Email address.");
    return;
  }

  if (!/^\d{12}$/.test(formData.aadhaar || "")) {
    toast.error("Aadhaar must be exactly 12 digits.");
    return;
  }

  if (!/^[6-9]\d{9}$/.test(formData.mobile || "")) {
    toast.error("Mobile number must be 10 digits starting with 6–9.");
    return;
  }

  const validBloodGroups = ["a+", "a-", "b+", "b-", "ab+", "ab-", "o+", "o-"];
  if (!validBloodGroups.includes((formData.bloodGroup || "").toLowerCase())) {
    toast.error("Please select a valid Blood Group.");
    return;
  }

  if (!formData.emergencyContactName || formData.emergencyContactName.trim().length < 3) {
    toast.error("Emergency Contact Name is too short.");
    return;
  }

  if (!/^[6-9]\d{9}$/.test(formData.emergencyContactNumber || "")) {
    toast.error("Enter a valid Emergency Contact Number.");
    return;
  }

  if (!formData.emergencyContactRelation || formData.emergencyContactRelation.trim().length < 2) {
    toast.error("Enter a valid Emergency Contact Relation.");
    return;
  }

  if (!formData.department) {
    toast.error("Department is required.");
    return;
  }

  if (!formData.location) {
    toast.error("Location is required.");
    return;
  }

  if (!formData.weekendPolicy) {
    toast.error("Weekend Policy is required.");
    return;
  }

  if (!formData.designation || formData.designation.trim().length < 2) {
    toast.error("Designation is required.");
    return;
  }

  const validEmploymentStatus = ["probation", "confirmed", "internship"];
  if (!validEmploymentStatus.includes((formData.employmentStatus || "").toLowerCase())) {
    toast.error("Select a valid Employment Status.");
    return;
  }

  if (!formData.bankName || formData.bankName.trim().length < 2) {
    toast.error("Bank Name is required.");
    return;
  }

  if (!formData.branch || formData.branch.trim().length < 2) {
    toast.error("Branch is required.");
    return;
  }

  if (!/^\d{9,18}$/.test(formData.accountNo || "")) {
    toast.error("Account Number must be between 9 and 18 digits.");
    return;
  }

  if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc || "")) {
    toast.error("IFSC Code format is invalid. Example: HDFC0001234");
    return;
  }

  if (!formData.accountHolderName || formData.accountHolderName.trim().length < 3) {
    toast.error("Account Holder Name is required.");
    return;
  }

  try {
    const resultAction = await dispatch(submitEmployeeDetails(formData));
    if (submitEmployeeDetails.fulfilled.match(resultAction)) {
      setFormData({});
      setCurrentStep(1);
    }
  } catch (error) {
    toast.error("Failed to submit. Please try again.");
  }
};


  const steps = ["Basic Info", "Position Details", "Payment Mode"];

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1BasicInfo data={formData} onUpdate={handleUpdate} />;
      case 2:
        return (
          <Step2EmployeePosition data={formData} onUpdate={handleUpdate} />
        );
      case 3:
        return <Step3PaymentMode data={formData} onUpdate={handleUpdate} />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-2 bg-white rounded-lg shadow">
      {/* Progress Indicator */}
      <div className="mb-6">
        <ProgressIndicator steps={steps} currentStep={currentStep - 1} />
      </div>

      {/* Step Form */}
      {renderStep()}

      <div className="mt-8 flex gap-4 justify-end">
        {currentStep > 1 && (
          <button
            onClick={() => setCurrentStep(currentStep - 1)}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
          >
            Previous
          </button>
        )}

        {currentStep < 3 ? (
          <button
            onClick={() => setCurrentStep(currentStep + 1)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Next
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            Submit
          </button>
        )}
      </div>
    </div>
  );
};

export default AddEmployeeForm;
